import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";
import { ElMessage } from "element-plus";
import { useAuthStore } from "@/stores/auth";

export interface ApiEnvelope<T = unknown> {
  code: number;
  msg: string;
  time: number;
  data: T;
}

/**
 * 开发态强制走同源 /index.php，经 Vite 代理转发并忽略 SSL 校验。
 * 浏览器里没法“忽略证书”，只能不直连 https。
 */
function resolveApiBaseUrl(): string {
  const raw = import.meta.env.VITE_API_URL || "/index.php/";
  if (!import.meta.env.DEV) {
    return raw;
  }
  if (raw.startsWith("http://") || raw.startsWith("https://")) {
    try {
      const pathname = new URL(raw).pathname || "/index.php/";
      return pathname.endsWith("/") ? pathname : `${pathname}/`;
    } catch {
      return "/index.php/";
    }
  }
  return raw;
}

const http = axios.create({
  baseURL: resolveApiBaseUrl(),
  headers: {
    "Content-Type": "application/x-www-form-urlencoded"
  }
});

function forceLogout(message?: string) {
  const auth = useAuthStore();
  if (!auth.authorization && !auth.user.name) {
    return;
  }
  auth.clearSession();
  void import("@/stores/favorite").then(({ useFavoriteStore }) => {
    useFavoriteStore().setEditMode(false);
    useFavoriteStore().stopProbePolling();
  });
  if (message) {
    ElMessage({
      showClose: true,
      message,
      type: "warning"
    });
  }
  void import("@/router").then(({ default: router }) => {
    if (router.currentRoute.value.path !== "/admin/login") {
      void router.push("/admin/login");
    }
  });
}

function isAuthFailureCode(code: number): boolean {
  return code === 401 || code === 403;
}

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const auth = useAuthStore();
  auth.clearIfExpired();
  if (auth.authorization) {
    config.headers.Authorization = auth.authorization;
  }
  return config;
});

http.interceptors.response.use(
  (response) => {
    const authHeader =
      response.headers.authorization || response.headers.Authorization;
    if (authHeader) {
      useAuthStore().setAuthorization(String(authHeader));
    }

    const body = response.data as ApiEnvelope;
    if (body && typeof body === "object" && "code" in body && body.code !== 0) {
      // 后端常用 HTTP 200 + code=401 表示鉴权失败
      if (isAuthFailureCode(Number(body.code))) {
        forceLogout(body.msg || "登录已失效，请重新登录");
        return Promise.reject(body);
      }
      ElMessage({
        showClose: true,
        message: body.msg || "请求失败",
        type: "error"
      });
      return Promise.reject(body);
    }
    return body as unknown as typeof response;
  },
  (error: AxiosError) => {
    const status = error.response?.status;
    if (status === 401 || status === 403) {
      const data = error.response?.data as { msg?: string } | undefined;
      forceLogout(data?.msg || "登录已失效，请重新登录");
    } else {
      const data = error.response?.data as { msg?: string } | undefined;
      ElMessage({
        showClose: true,
        message: data?.msg || error.message || "网络请求失败",
        type: "error"
      });
    }
    return Promise.reject(error);
  }
);

export default http;
